export enum KVInputActionKey {
  LeftClick = 'LeftMouseDown',
  RightClick = 'RightMouseDown',
  LeftClickSpacebar = 'LeftMouseDownWithSpacebar',
}

export type KVInputProfile = {
  primaryKey: KVInputActionKey
  secondaryKey: KVInputActionKey
}

const DEFAULT_KV_INPUT_PROFILE: KVInputProfile = {
  primaryKey: KVInputActionKey.RightClick,
  secondaryKey: KVInputActionKey.LeftClick,
}

export const KV_INPUT_ACTION_LABELS: Record<KVInputActionKey, string> = {
  [KVInputActionKey.LeftClick]: 'Left Click',
  [KVInputActionKey.RightClick]: 'Right Click',
  [KVInputActionKey.LeftClickSpacebar]: 'Left Click + Spacebar',
}

export enum KVResolutionQuality {
  Low = 0.75,
  Standard = 1.0,
  High = 1.5,
}

export type KVUserSettingsJSON = {
  orbitSpeed: number
  fieldOfView: number
  inputProfile?: Partial<Record<keyof KVInputProfile, string>>
  orbitCenterPersistent?: boolean
}

export class KVUserSettings {
  orbitSpeed: number
  fieldOfView: number
  inputProfile: KVInputProfile
  /** When the photo panel closes, restore orbit center persistent to this value. */
  orbitCenterPersistent: boolean

  constructor() {
    this.orbitSpeed = 0.7
    this.fieldOfView = 90
    this.inputProfile = DEFAULT_KV_INPUT_PROFILE
    this.orbitCenterPersistent = false
  }

  static fromJSON(json: KVUserSettingsJSON): KVUserSettings {
    const settings = new KVUserSettings()
    if (json.orbitSpeed) settings.orbitSpeed = json.orbitSpeed
    if (json.fieldOfView) settings.fieldOfView = json.fieldOfView
    settings.inputProfile = normalizeKVInputProfile(json.inputProfile)
    if (typeof json.orbitCenterPersistent === 'boolean') {
      settings.orbitCenterPersistent = json.orbitCenterPersistent
    }
    return settings
  }
}

const isKVInputActionKey = (value: unknown): value is KVInputActionKey =>
  Object.values(KVInputActionKey).includes(value as KVInputActionKey)

const normalizeKVInputActionKey = (
  value: unknown
): KVInputActionKey | undefined => {
  if (value === 'SpacePressed') {
    return KVInputActionKey.LeftClickSpacebar
  }
  return isKVInputActionKey(value) ? value : undefined
}

const getFallbackSecondaryKey = (
  primaryKey: KVInputActionKey
): KVInputActionKey =>
  primaryKey === KVInputActionKey.LeftClick
    ? KVInputActionKey.RightClick
    : KVInputActionKey.LeftClick

export const normalizeKVInputProfile = (
  inputProfile: KVUserSettingsJSON['inputProfile']
): KVInputProfile => {
  if (!inputProfile || typeof inputProfile !== 'object') {
    return DEFAULT_KV_INPUT_PROFILE
  }

  const primaryKey =
    normalizeKVInputActionKey(inputProfile.primaryKey) ??
    DEFAULT_KV_INPUT_PROFILE.primaryKey
  const requestedSecondaryKey =
    normalizeKVInputActionKey(inputProfile.secondaryKey) ??
    DEFAULT_KV_INPUT_PROFILE.secondaryKey

  return {
    primaryKey,
    secondaryKey:
      requestedSecondaryKey === primaryKey
        ? getFallbackSecondaryKey(primaryKey)
        : requestedSecondaryKey,
  }
}
